import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaEye, FaPhoneAlt, FaPlus, FaTrashAlt, FaWhatsapp } from "react-icons/fa";
import { FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import {
  ClearLeadDetail,
  DeleteLeadSlice,
  GetAllLeadsSlice,
  GetLeadDetailSlice,
} from "../../redux/features/LeadManagementSlice";
import AddLeadModal from "./AddLeadModal.jsx";
import LeadDetailModal from "./LeadDetailModal.jsx";
import StatusBadge from "./components/StatusBadge.jsx";
import ConfirmDialog from "./components/ConfirmDialog.jsx";
import {
  LEAD_STATUSES,
  eventCategories,
  formatDate,
  formatTime,
  getInitials,
  getStatusMeta,
  getWhatsAppUrl,
  isPastDate,
  isToday,
} from "./leadConstants";
import "./lead-management.css";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const MAX_VISIBLE_SERVICES = 2;

const FOLLOWUP_FILTERS = [
  { value: "", label: "All follow-ups" },
  { value: "today", label: "Due today" },
  { value: "overdue", label: "Overdue" },
  { value: "upcoming", label: "Upcoming" },
  { value: "none", label: "Not scheduled" },
];

const initialFilters = { search: "", status: "", category: "", followup: "" };

const matchesFollowup = (lead, filter) => {
  const date = lead?.nextFollowupDate;
  switch (filter) {
    case "today":
      return isToday(date);
    case "overdue":
      return Boolean(date) && isPastDate(date);
    case "upcoming":
      return Boolean(date) && !isPastDate(date) && !isToday(date);
    case "none":
      return !date;
    default:
      return true;
  }
};

const FollowupCell = ({ date }) => {
  if (!date) return <span className="lm-muted">Not set</span>;
  if (isToday(date)) return <span className="lm-pill lm-pill--warning">Today</span>;
  if (isPastDate(date)) return <span className="lm-pill lm-pill--danger">{formatDate(date)}</span>;
  return <span>{formatDate(date)}</span>;
};

const SkeletonRows = ({ rows = 6, cols }) =>
  Array.from({ length: rows }).map((_, rowIndex) => (
    <tr key={rowIndex} className="lm-skeleton-row">
      {Array.from({ length: cols }).map((__, colIndex) => (
        <td key={colIndex}>
          <span className="lm-skeleton" />
        </td>
      ))}
    </tr>
  ));

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

  // Insert ellipsis markers where pages are skipped
  return sorted.reduce((acc, page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) acc.push(`gap-${page}`);
    acc.push(page);
    return acc;
  }, []);
};

const LeadManagement = () => {
  const dispatch = useDispatch();
  const { loading, leads = [], leadDetail, deletingId } = useSelector(
    (state) => state.leadManagement || {}
  );

  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [leadToDelete, setLeadToDelete] = useState(null);

  const fetchLeads = useCallback(() => dispatch(GetAllLeadsSlice()), [dispatch]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // ---------- Derived data ----------

  const statusCounts = useMemo(
    () =>
      leads.reduce((acc, lead) => {
        acc[lead.status] = (acc[lead.status] || 0) + 1;
        return acc;
      }, {}),
    [leads]
  );

  const filteredLeads = useMemo(() => {
    const term = filters.search.trim().toLowerCase();

    return leads.filter((lead) => {
      if (filters.status && lead.status !== filters.status) return false;
      if (filters.category && lead.categoryName?.toLowerCase() !== filters.category.toLowerCase()) return false;
      if (!matchesFollowup(lead, filters.followup)) return false;
      if (!term) return true;

      return [lead.name, lead.phoneno, lead.categoryName, lead.decorationName, lead.address]
        .some((field) => field?.toLowerCase().includes(term));
    });
  }, [leads, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const paginatedLeads = filteredLeads.slice(pageStart, pageStart + pageSize);

  const hasActiveFilters = Object.values(filters).some(Boolean);

  // Prefer the freshly fetched detail; fall back to list data so the modal opens instantly
  const selectedLead = useMemo(() => {
    if (!selectedLeadId) return null;
    if (leadDetail?._id === selectedLeadId) return leadDetail;
    return leads.find((lead) => lead._id === selectedLeadId) || null;
  }, [selectedLeadId, leadDetail, leads]);

  // ---------- Handlers ----------

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setPage(1);
  };

  const openLead = (id) => {
    setSelectedLeadId(id);
    dispatch(GetLeadDetailSlice(id));
  };

  const closeLead = useCallback(() => {
    setSelectedLeadId(null);
    dispatch(ClearLeadDetail());
  }, [dispatch]);

  const confirmDelete = async () => {
    if (!leadToDelete) return;
    try {
      await dispatch(DeleteLeadSlice(leadToDelete._id)).unwrap();
      setLeadToDelete(null);
    } catch {
      // Error toast is handled in the slice
    }
  };

  const handleLeadAdded = () => {
    resetFilters();
  };

  const COLUMN_COUNT = 9;

  return (
    <div className="WrapperArea">
      <div className="WrapperBox lm-page">
        {/* ---------- Header ---------- */}
        <div className="lm-page-header">
          <div>
            <h2>Lead Management</h2>
            <p className="lm-muted">Track enquiries, follow up with customers and convert them into bookings</p>
          </div>
          <div className="lm-page-actions">
            <button
              type="button"
              className="lm-btn lm-btn--outline"
              onClick={fetchLeads}
              disabled={loading}
              title="Refresh leads"
            >
              <FiRefreshCw className={loading ? "lm-spin" : ""} /> Refresh
            </button>
            <button type="button" className="lm-btn lm-btn--primary" onClick={() => setIsAddOpen(true)}>
              <FaPlus size={11} /> Add Lead
            </button>
          </div>
        </div>

        {/* ---------- Status summary (doubles as quick filter) ---------- */}
        <div className="lm-stats">
          <button
            type="button"
            className={`lm-stat ${!filters.status ? "is-active" : ""}`}
            style={{ "--lm-status": "#111827", "--lm-status-bg": "#f3f4f6" }}
            onClick={() => updateFilter("status", "")}
          >
            <span className="lm-stat-label">All Leads</span>
            <span className="lm-stat-value">{leads.length}</span>
          </button>

          {LEAD_STATUSES.map((status) => (
            <button
              key={status.value}
              type="button"
              className={`lm-stat ${filters.status === status.value ? "is-active" : ""}`}
              style={{ "--lm-status": status.color, "--lm-status-bg": status.bg }}
              onClick={() => updateFilter("status", filters.status === status.value ? "" : status.value)}
            >
              <span className="lm-stat-label">
                <span className="lm-status-dot" /> {status.label}
              </span>
              <span className="lm-stat-value">{statusCounts[status.value] || 0}</span>
            </button>
          ))}
        </div>

        {/* ---------- Table card ---------- */}
        <div className="lm-panel">
          <div className="lm-toolbar">
            <div className="lm-search">
              <FiSearch className="lm-search-icon" />
              <input
                type="search"
                placeholder="Search by name, phone, category, address..."
                value={filters.search}
                onChange={(e) => updateFilter("search", e.target.value)}
              />
            </div>

            <select
              className="lm-input lm-select lm-toolbar-select"
              value={filters.category}
              onChange={(e) => updateFilter("category", e.target.value)}
              aria-label="Filter by category"
            >
              <option value="">All categories</option>
              {eventCategories.map((category) => (
                <option key={category.id} value={category.name}>{category.name}</option>
              ))}
            </select>

            <select
              className="lm-input lm-select lm-toolbar-select"
              value={filters.followup}
              onChange={(e) => updateFilter("followup", e.target.value)}
              aria-label="Filter by follow-up"
            >
              {FOLLOWUP_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            {hasActiveFilters && (
              <button type="button" className="lm-btn lm-btn--ghost" onClick={resetFilters}>
                <FiX /> Clear filters
              </button>
            )}
          </div>

          <div className="lm-table-wrap">
            <table className="lm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Event</th>
                  <th>Services</th>
                  <th>Event Date</th>
                  <th>Status</th>
                  <th>Next Follow-up</th>
                  <th>Created</th>
                  <th className="lm-text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && leads.length === 0 ? (
                  <SkeletonRows cols={COLUMN_COUNT} />
                ) : paginatedLeads.length > 0 ? (
                  paginatedLeads.map((lead, index) => {
                    const statusMeta = getStatusMeta(lead.status);
                    const services = Array.isArray(lead.services) ? lead.services : [];
                    const whatsappUrl = getWhatsAppUrl(lead.phoneno, lead.name);
                    const isDeleting = deletingId === lead._id;

                    return (
                      <tr key={lead._id} className={isDeleting ? "is-deleting" : ""}>
                        <td className="lm-muted">{pageStart + index + 1}</td>

                        <td>
                          <div className="lm-customer">
                            <span
                              className="lm-avatar"
                              style={{ "--lm-status": statusMeta.color, "--lm-status-bg": statusMeta.bg }}
                            >
                              {getInitials(lead.name)}
                            </span>
                            <div className="lm-customer-info">
                              <button type="button" className="lm-customer-name" onClick={() => openLead(lead._id)}>
                                {lead.name || "Unnamed Lead"}
                              </button>
                              <span className="lm-muted">+91 {lead.phoneno}</span>
                              {lead.address && (
                                <span className="lm-muted lm-truncate" title={lead.address}>{lead.address}</span>
                              )}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="lm-stack">
                            <span className="lm-strong">{lead.categoryName || "—"}</span>
                            {lead.decorationName && (
                              <span className="lm-muted lm-truncate" title={lead.decorationName}>
                                {lead.decorationName}
                              </span>
                            )}
                          </div>
                        </td>

                        <td>
                          {services.length > 0 ? (
                            <div className="lm-chip-list" title={services.join(", ")}>
                              {services.slice(0, MAX_VISIBLE_SERVICES).map((service) => (
                                <span key={service} className="lm-chip lm-chip--sm">{service}</span>
                              ))}
                              {services.length > MAX_VISIBLE_SERVICES && (
                                <span className="lm-chip lm-chip--sm lm-chip--more">
                                  +{services.length - MAX_VISIBLE_SERVICES}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="lm-muted">—</span>
                          )}
                        </td>

                        <td>{lead.eventDate ? formatDate(lead.eventDate) : <span className="lm-muted">—</span>}</td>

                        <td><StatusBadge status={lead.status} /></td>

                        <td><FollowupCell date={lead.nextFollowupDate} /></td>

                        <td>
                          <div className="lm-stack">
                            <span>{formatDate(lead.createdAt)}</span>
                            <span className="lm-muted">{formatTime(lead.createdAt)}</span>
                          </div>
                        </td>

                        <td>
                          <div className="lm-row-actions">
                            <button
                              type="button"
                              className="lm-action lm-action--view"
                              onClick={() => openLead(lead._id)}
                              title="View & update lead"
                            >
                              <FaEye />
                            </button>
                            {whatsappUrl && (
                              <a
                                href={whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="lm-action lm-action--whatsapp"
                                title={`Chat on WhatsApp with ${lead.name || lead.phoneno}`}
                              >
                                <FaWhatsapp />
                              </a>
                            )}
                            <a
                              href={`tel:${lead.phoneno}`}
                              className="lm-action lm-action--call"
                              title={`Call ${lead.phoneno}`}
                            >
                              <FaPhoneAlt size={12} />
                            </a>
                            <button
                              type="button"
                              className="lm-action lm-action--delete"
                              onClick={() => setLeadToDelete(lead)}
                              disabled={isDeleting}
                              title="Delete lead"
                            >
                              <FaTrashAlt size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={COLUMN_COUNT}>
                      <div className="lm-empty">
                        <h5>{hasActiveFilters ? "No leads match your filters" : "No leads yet"}</h5>
                        <p className="lm-muted">
                          {hasActiveFilters
                            ? "Try adjusting the search or filters."
                            : "New enquiries will appear here. You can also add one manually."}
                        </p>
                        {hasActiveFilters ? (
                          <button type="button" className="lm-btn lm-btn--outline" onClick={resetFilters}>
                            Clear filters
                          </button>
                        ) : (
                          <button type="button" className="lm-btn lm-btn--primary" onClick={() => setIsAddOpen(true)}>
                            <FaPlus size={11} /> Add Lead
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ---------- Pagination ---------- */}
          {filteredLeads.length > 0 && (
            <div className="lm-pagination">
              <div className="lm-muted">
                Showing <strong>{pageStart + 1}</strong>–
                <strong>{Math.min(pageStart + pageSize, filteredLeads.length)}</strong> of{" "}
                <strong>{filteredLeads.length}</strong> leads
              </div>

              <div className="lm-pagination-controls">
                <select
                  className="lm-input lm-select lm-page-size"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  aria-label="Rows per page"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </select>

                <button
                  type="button"
                  className="lm-page-btn"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                {getPageNumbers(currentPage, totalPages).map((item) =>
                  typeof item === "number" ? (
                    <button
                      key={item}
                      type="button"
                      className={`lm-page-btn ${item === currentPage ? "is-active" : ""}`}
                      onClick={() => setPage(item)}
                    >
                      {item}
                    </button>
                  ) : (
                    <span key={item} className="lm-page-gap">…</span>
                  )
                )}
                <button
                  type="button"
                  className="lm-page-btn"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddLeadModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} onSuccess={handleLeadAdded} />

      <LeadDetailModal lead={selectedLead} isOpen={Boolean(selectedLeadId)} onClose={closeLead} />

      <ConfirmDialog
        isOpen={Boolean(leadToDelete)}
        title="Delete lead?"
        message={
          <>
            This will permanently remove <strong>{leadToDelete?.name || leadToDelete?.phoneno}</strong> and all
            follow-up history. This action cannot be undone.
          </>
        }
        confirmLabel="Delete Lead"
        loading={Boolean(deletingId)}
        onConfirm={confirmDelete}
        onCancel={() => setLeadToDelete(null)}
      />
    </div>
  );
};

export default LeadManagement;
